// What? A React component for managing source documents in document type creation/editing.
// Why? To allow users to define which documents are needed as inputs for document generation.
// How? The component's logic flow:
// 1. Load: Fetch existing source documents when editing a document type
// 2. Add: Allow users to add new source document definitions
// 3. Edit: Enable editing of display label, type, required flag, and help text
// 4. Remove: Support deletion of source documents (with API cleanup if persisted)
// 5. Validate: Ensure required fields are filled with proper constraints

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

import Loading from "../../../../../components/Loading";
import { useDocumentSources } from "../../../hooks/useDocumentSources";
import { useValidation } from "../../../contexts/ValidationContext";
import { SourceDocumentDefinition } from "../../../types/documentTypes";
import { useFeatureFlag } from "../../../../../providers/FeatureFlagProvider";

interface SourceDocumentsTabProps {
  documents: SourceDocumentDefinition[];
  documentTypeId?: number;
  workspace_id: string;
  onChange: (docs: SourceDocumentDefinition[], deletedIds?: number[]) => void;
  showValidation: boolean;
}

const SourceDocumentsTab: React.FC<SourceDocumentsTabProps> = ({
  documents,
  documentTypeId,
  workspace_id,
  onChange,
  showValidation,
}) => {
  const { setShowValidation } = useValidation();
  const transcriptsEnabled = useFeatureFlag("meridian_transcripts");
  const { getSources, loading, deleteSource } = useDocumentSources();
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Load existing sources only once when editing
  useEffect(() => {
    const loadSources = async () => {
      if (documentTypeId && !initialLoadDone) {
        try {
          const sources = await getSources(documentTypeId, workspace_id);
          const formattedSources: SourceDocumentDefinition[] = sources.map(
            (source) => ({
              SourceFieldID: source.SourceFieldID,
              Name: source.Name,
              Label: source.Label,
              DocumentType: source.DocumentType,
              IsRequired: source.IsRequired,
              OrderIndex: source.OrderIndex,
              HelpText: source.HelpText || "",
            }),
          );
          onChange(formattedSources);
          setInitialLoadDone(true);
        } catch (error) {
          console.error("Failed to load sources:", error);
        }
      }
    };
    loadSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- workspace_id accessed via closure
  }, [documentTypeId, initialLoadDone, getSources, onChange]);

  const handleAddDocument = () => {
    setShowValidation(false);

    const newDoc: SourceDocumentDefinition = {
      Name: "",
      Label: "",
      DocumentType: "Source",
      IsRequired: false,
      OrderIndex: documents.length,
      HelpText: "",
    };

    onChange([...documents, newDoc]);
  };

  const formatFieldName = (label: string): string => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const handleUpdateDocument = (
    index: number,
    updates: Partial<SourceDocumentDefinition>,
  ) => {
    const updatedDocs = [...documents];
    const currentDoc = updatedDocs[index];

    // Reset validation when user starts typing
    if ("Label" in updates) {
      setShowValidation(false);
    }

    updatedDocs[index] = {
      ...currentDoc,
      ...updates,
      ...(currentDoc.SourceFieldID && {
        SourceFieldID: currentDoc.SourceFieldID,
      }),
    };

    if ("Label" in updates && updates.Label) {
      updatedDocs[index].Name = formatFieldName(updates.Label);
    }

    onChange(updatedDocs);
  };

  const handleRemoveDocument = async (index: number) => {
    const doc = documents[index];
    const updatedDocs = documents.filter((_, i) => i !== index);
    const reorderedDocs = updatedDocs.map((doc, idx) => ({
      ...doc,
      OrderIndex: idx,
    }));

    if (doc.SourceFieldID && documentTypeId) {
      try {
        await deleteSource(documentTypeId, doc.SourceFieldID, workspace_id);
        onChange(reorderedDocs);
      } catch (error) {
        console.error("Failed to delete source:", error);
        return;
      }
    } else {
      onChange(reorderedDocs);
    }
  };

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const items = Array.from(documents);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update OrderIndex for all items
      const updatedDocs = items.map((doc, index) => ({
        ...doc,
        OrderIndex: index,
      }));

      onChange(updatedDocs);
    },
    [documents, onChange],
  );

  if (loading && !initialLoadDone) {
    return <Loading />;
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography level="h3">Source Documents</Typography>
        <Button
          variant="solid"
          color="primary"
          onClick={handleAddDocument}
          size="sm"
        >
          + Add Document
        </Button>
      </Stack>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="source-documents">
          {(provided) => (
            <Stack
              spacing={2}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {documents.map((doc, index) => {
                // Validation logic for Display Label
                let labelError: string | undefined = undefined;
                const trimmed = doc.Label?.trim();
                if (showValidation) {
                  if (!trimmed) labelError = "Display Label is required";
                  else if (trimmed.length < 3)
                    labelError = "Label must be at least 3 characters";
                  else if (trimmed.length > 100)
                    labelError = "Label must be less than 100 characters";
                }

                const draggableId = doc.SourceFieldID
                  ? String(doc.SourceFieldID)
                  : `new-source-${index}`;

                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: snapshot.isDragging
                            ? "primary.400"
                            : "neutral.300",
                          borderRadius: "sm",
                          bgcolor: snapshot.isDragging
                            ? "background.level1"
                            : "background.surface",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "neutral.400",
                            boxShadow: "sm",
                          },
                          ...provided.draggableProps.style,
                        }}
                      >
                        <Stack spacing={2}>
                          {/* Top row: Drag handle, Label, Type, Required checkbox */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "auto 1fr",
                                sm: "auto 1fr 1fr",
                                md: "auto minmax(0, 2fr) minmax(0, 1fr) auto",
                              },
                              gap: 2,
                              alignItems: "start",
                            }}
                          >
                            {/* Drag handle */}
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "grab",
                                color: "text.tertiary",
                                mt: 3.5,
                                "&:hover": {
                                  color: "text.secondary",
                                },
                                "&:active": {
                                  cursor: "grabbing",
                                },
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>

                            <FormControl required error={!!labelError}>
                              <FormLabel>Display Label</FormLabel>
                              <Input
                                value={doc.Label}
                                onChange={(e) =>
                                  handleUpdateDocument(index, {
                                    Label: e.target.value,
                                  })
                                }
                                placeholder="e.g., Resume, Cover Letter"
                                slotProps={{ input: { maxLength: 100 } }}
                                size="lg"
                              />
                              {labelError && (
                                <FormHelperText>{labelError}</FormHelperText>
                              )}
                            </FormControl>

                            <FormControl>
                              <FormLabel>Type</FormLabel>
                              {doc.DocumentType === "Transcript" &&
                              !transcriptsEnabled ? (
                                <Typography level="body-md" sx={{ py: 1 }}>
                                  Transcript unavailable while Meridian
                                  transcripts are disabled
                                </Typography>
                              ) : (
                                <Select
                                  value={doc.DocumentType}
                                  onChange={(_, newType) => {
                                    if (newType) {
                                      handleUpdateDocument(index, {
                                        DocumentType: newType as
                                          | "Source"
                                          | "Example"
                                          | "Guidance"
                                          | "Transcript",
                                      });
                                    }
                                  }}
                                  size="lg"
                                >
                                  <Option value="Source">Source</Option>
                                  <Option value="Example">Example</Option>
                                  <Option value="Guidance">Guidance</Option>
                                  {transcriptsEnabled && (
                                    <Option value="Transcript">
                                      Transcript
                                    </Option>
                                  )}
                                </Select>
                              )}
                            </FormControl>

                            <FormControl
                              orientation="horizontal"
                              sx={{ alignSelf: "end", pb: 0.5 }}
                            >
                              <Checkbox
                                checked={doc.IsRequired}
                                onChange={(e) =>
                                  handleUpdateDocument(index, {
                                    IsRequired: e.target.checked,
                                  })
                                }
                                label="Required"
                              />
                            </FormControl>
                          </Box>

                          {/* Bottom row: Help text and delete button */}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <FormControl sx={{ flex: 1 }}>
                              <FormLabel>Help Text</FormLabel>
                              <Input
                                value={doc.HelpText || ""}
                                onChange={(e) =>
                                  handleUpdateDocument(index, {
                                    HelpText: e.target.value,
                                  })
                                }
                                placeholder="Optional guidance for users uploading this document"
                                size="lg"
                              />
                            </FormControl>
                            <Tooltip title="Remove Document">
                              <IconButton
                                color="danger"
                                variant="plain"
                                onClick={() => handleRemoveDocument(index)}
                                sx={{ mt: 3.5 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}

              {(!documents || documents.length === 0) && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography level="body-md" color="neutral" sx={{ mb: 2 }}>
                    No source documents added yet.
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    Click "+ Add Document" to define what documents users need
                    to provide.
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
  );
};

export default SourceDocumentsTab;
